'use client';

import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

export function TagsInput({
  label,
  hint,
  values,
  onChange,
  placeholder = 'Type and press Enter',
}: {
  label: string;
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');

  const addTag = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    if (values.some((v) => v.toLowerCase() === value.toLowerCase())) return;
    onChange([...values, value]);
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === 'Backspace' && !draft && values.length) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="ew-field">
      <label className="ew-field-label">{label}</label>
      <div className="flex min-h-[2.75rem] flex-wrap items-center gap-2 rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 focus-within:border-[var(--color-primary)] focus-within:shadow-[0_0_0_3px_rgba(152,170,157,0.18)]">
        {values.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--color-surface-muted)] px-2.5 py-1 text-xs font-medium text-[var(--color-text)] border border-[var(--color-border)]"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(values.filter((v) => v !== tag))}
              className="rounded-full p-0.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
              aria-label={`Remove ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          className="min-w-[120px] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-[var(--color-text)] outline-none"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => addTag(draft)}
          placeholder={values.length ? '' : placeholder}
        />
      </div>
      {hint && <p className="ew-field-hint">{hint}</p>}
    </div>
  );
}

export function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  compact = false,
  disabled = false,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  compact?: boolean;
  disabled?: boolean;
}) {
  return (
    <label className={`ew-toggle-row${compact ? ' ew-toggle-row--compact' : ''}${disabled ? ' opacity-60 cursor-not-allowed' : ''}`}>
      <div className="min-w-0">
        <span className="ew-toggle-title">{label}</span>
        <span className="ew-toggle-hint">{hint}</span>
      </div>
      <input
        type="checkbox"
        disabled={disabled}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[rgba(152,170,157,0.3)] disabled:cursor-not-allowed"
        checked={checked}
        onChange={(e) => {
          if (!disabled) onChange(e.target.checked);
        }}
      />
    </label>
  );
}

export function RadioCard({
  label,
  selected,
  onSelect,
  disabled = false,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onSelect();
      }}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left text-sm font-medium transition-all ${
        disabled
          ? 'cursor-not-allowed border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] opacity-50'
          : selected
            ? 'border-[var(--color-primary)] bg-[rgba(152,170,157,0.12)] text-[var(--color-text)] shadow-sm'
            : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:border-[rgba(152,170,157,0.45)] hover:bg-[var(--color-surface-muted)]'
      }`}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
          disabled
            ? 'border-[var(--color-border)]'
            : selected
              ? 'border-[var(--color-primary)]'
              : 'border-[var(--color-border)]'
        }`}
        aria-hidden
      >
        {selected && !disabled ? (
          <span className="h-2 w-2 rounded-full bg-[var(--color-primary)]" />
        ) : null}
      </span>
      {label}
    </button>
  );
}
